import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
  inject,
  ViewEncapsulation
} from '@angular/core';
import { fromEvent, interval, Subject, Subscription, throwError } from 'rxjs';
import { StorageService } from '@Shared/services';
import { AppSettings } from '../../app.settings';
import { RecorderErrors } from './errors.enum';
import { PitchCardTypeEnum } from '@App/common/enums';
import { CreatePitchCardService } from '@App/modules/create-pitch-card/create-pitch-card.service';
import { UserCommonApiService } from '@App/common/services';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  isPlatformBrowser,
  NgClass,
  NgIf,
  NgOptimizedImage,
  NgStyle
} from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { CountdownComponent } from 'ngx-countdown';

@Component({
  selector: 'app-video-recorder',
  templateUrl: './video-recorder.component.html',
  standalone: true,
  imports: [
    FormsModule,
    DialogModule,
    ButtonModule,
    NgClass,
    NgStyle,
    NgIf,
    NgOptimizedImage,
    CountdownComponent
  ],
  styleUrls: ['./video-recorder.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class VideoRecorderComponent
  implements AfterViewInit, OnInit, OnDestroy
{
  @ViewChild('videoElement')
  video!: ElementRef;
  @ViewChild('videoRecordedElement')
  videoRecorded!: ElementRef;
  @ViewChild('countdown')
  countdownTop!: CountdownComponent;
  @ViewChild('beforeRecordCountdown')
  beforeRecordCountdown!: CountdownComponent;
  @ViewChild('textarea')
  textarea!: ElementRef;
  @ViewChild('textareaContainer')
  textareaContainer!: ElementRef;
  @ViewChild('recorderWrapper')
  recorderWrapper!: ElementRef;
  @Output()
  closeVideo = new EventEmitter<boolean>();
  @Output()
  saveVideo = new EventEmitter<File>();
  public countdownTopLeftTime = 59;
  public isActiveCoundown = false;
  public recorderWrapperEl: any;
  public isRecording = false;
  public isRecorded = false;
  public isPlaying = false;
  public isQuestions = false;
  public isTeleprompter = true;
  public isSlowScroll = false;
  public userFullName: string;
  public timerCountdown = 5;
  public isTenSecondsLeft = false;
  public isModalError: boolean;
  public modalErrorText: string;
  public isPermissionDenied = false;
  public isNotSupportedBrowser = false;
  public isMobile = false;
  pitchcardType: PitchCardTypeEnum;
  @Input() teleprompter: any;
  width: number = 300;
  height: number = 200;
  top: number = 50;
  left: number = 10;
  isDragging: boolean = false;
  startLeft: number = 10;
  startTop: number = 50;
  isTeleprompterEditing: boolean;
  isTeleprompterHasScroll: boolean = true;
  fontSize: number = 18;
  scrollSpeed: number = 100;

  isResizing: boolean = false;
  initialX: number = 0;
  initialY: number = 0;
  initialWidth: number = 300;
  initialHeight: number = 200;
  resizeDirection: string = '';
  lastLeft: number = 10;
  lastTop: number = 50;
  intervalScroll: Subscription;
  public isFrontCamera: boolean = true;
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private videoElemet: HTMLVideoElement;
  private videoRecordedEl: HTMLVideoElement;
  private stm!: MediaStream;
  private mediaRecorder: any; // Changed from IMediaRecorder to any for SSR compatibility
  private blob!: Blob;
  private chunks = [];
  private $isDestroy: Subject<void> = new Subject<void>();
  private isBrowser: boolean;

  constructor(
    private userCommonService: UserCommonApiService,
    private storageService: StorageService,
    private createPitchCardService: CreatePitchCardService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  checkIfMobile(): void {
    if (!this.isBrowser) {
      this.isMobile = false;
      return;
    }

    const isTouchDevice = navigator.maxTouchPoints > 0;
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const isPortrait = screenHeight > screenWidth;
    const isSmallScreen = screenWidth <= 1024;
    this.isMobile = isTouchDevice && (isSmallScreen || isPortrait);
    const element = this.recorderWrapper?.nativeElement;
    if (this.left > element.offsetWidth - this.width) {
      this.width = element.offsetWidth - 40;
      this.left =
        (element.offsetWidth - this.initialWidth) / 2 >= 0
          ? (element.offsetWidth - this.initialWidth) / 2
          : 10;
      this.lastLeft =
        (element.offsetWidth - this.initialWidth) / 2 >= 0
          ? (element.offsetWidth - this.initialWidth) / 2
          : 10;
    }
    if (this.top > element.offsetHeight - this.height) {
      this.height = element.offsetHeight - 40;
      this.top = 10;
      this.lastTop = 10;
    }
    if (
      this.height > element.offsetHeight ||
      this.initialHeight > element.offsetHeight
    ) {
      this.height = element.offsetHeight - 30;
      this.initialHeight = element.offsetHeight - 30;
    }
    if (this.isMobile && !this.isFullscreen()) {
      this.enterFullscreen();
    } else if (!this.isMobile && this.isFullscreen()) {
      if (isPlatformBrowser(this.platformId)) {
        document.exitFullscreen();
      }
    }
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      fromEvent(window, 'resize')
        .pipe(debounceTime(300), takeUntil(this.$isDestroy))
        .subscribe(() => {
          this.checkIfMobile();
        });
    }
    this.initCamera();
    setTimeout(() => {
      this.checkIfMobile();
      if (isPlatformBrowser(this.platformId)) {
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const isPortrait = screenHeight > screenWidth;
        if (!isPortrait) {
          this.width = 400;
          this.height = 300;
          this.initialHeight = 300;
          this.initialWidth = 400;
        } else {
          this.width = 300;
          this.height = 400;
          this.initialHeight = 400;
          this.initialWidth = 300;
        }
      }
      const element = this.recorderWrapper?.nativeElement;
      this.left = (element.offsetWidth - this.initialWidth) / 2;
      this.top =
        (element.offsetHeight - this.initialHeight) / 2 - 20 >= 0
          ? (element.offsetHeight - this.initialHeight) / 2 - 20
          : 10;
      this.lastLeft = (element.offsetWidth - this.initialWidth) / 2;
      this.lastTop =
        (element.offsetHeight - this.initialHeight) / 2 - 20 >= 0
          ? (element.offsetHeight - this.initialHeight) / 2 - 20
          : 10;
    });
    const userDetails =
      this.storageService.getItem(AppSettings.USER_DETAILS) &&
      JSON.parse(this.storageService.getItem(AppSettings.USER_DETAILS));
    this.userCommonService
      .getUserProfile(userDetails.userId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        ({ data }) => (this.userFullName = `${data.firstName} ${data.lastName}`)
      );

    this.pitchcardType = this.createPitchCardService.pitchcardFormControl.controls.pitchcardType.value;
  }

  ngAfterViewInit(): void {
    this.videoElemet = this.video.nativeElement;
    this.recorderWrapperEl = this.recorderWrapper.nativeElement;
  }

  ngOnDestroy(): void {
    this.$isDestroy.next();
    this.$isDestroy.complete();
    this.closeRecorder();
  }

  isFullscreen(): boolean {
    return isPlatformBrowser(this.platformId)
      ? !!document.fullscreenElement
      : false;
  }

  public async initCamera(): Promise<void> {
    this.isModalError = false;

    try {
      const constraints: MediaStreamConstraints = {
        audio: true,
        video: {
          width: 640,
          height: 360,
          facingMode: this.isFrontCamera ? 'user' : 'environment'
        }
      };

      if (
        !isPlatformBrowser(this.platformId) ||
        !navigator.mediaDevices?.getUserMedia
      ) {
        this.modalErrorText = RecorderErrors.NAVIGATOR_NOT_SUPPORTED;
        this.isNotSupportedBrowser = true;
        this.isModalError = true;
        return;
      }

      if (this.stm) {
        this.stm.getTracks().forEach((track) => track.stop());
      }

      const stream: MediaStream =
        await navigator.mediaDevices.getUserMedia(constraints);

      this.stm = stream;
      this.videoElemet.srcObject = stream;
      this.videoElemet.style.transform = this.isFrontCamera
        ? 'scaleX(-1)'
        : 'scaleX(1)';
      this.videoElemet.style.objectFit = 'cover';
    } catch (error) {
      this.showModalError(`${error}`);
    }
  }

  public async toggleCamera(): Promise<void> {
    this.isFrontCamera = !this.isFrontCamera;
    await this.initCamera();
  }

  public startBeforeRecord(): void {
    if (this.stm) {
      this.isRecorded = false;
      this.timerCountdown = 5;
      this.isActiveCoundown = true;
      this.resetTeleprompterScroll();
      const intervalSec = interval(1000).subscribe(() => {
        this.timerCountdown--;
        this.cdr.detectChanges();
        if (this.timerCountdown === 0) {
          intervalSec.unsubscribe();
          this.isActiveCoundown = false;
          this.timerCountdown = 5;
          this.cdr.detectChanges();
          this.startRecord();
        }
      });
    } else {
      this.showModalError('noStream');
    }
  }

  public togglePlayer(): void {
    this.isPlaying ? this.videoRecordedEl.pause() : this.videoRecordedEl.play();
    this.isPlaying = !this.isPlaying;
  }

  public toggleQuestions(): void {
    this.isTeleprompter = false;
    this.isQuestions = !this.isQuestions;
  }

  public toggleTeleprompter(): void {
    this.isQuestions = false;
    this.isTeleprompter = !this.isTeleprompter;
  }

  public scrollTeleprompter(type?: string): void {
    if (
      type === 'pause' ||
      this.scrollSpeed !== 100 ||
      !this.intervalScroll?.closed
    ) {
      this.intervalScroll?.unsubscribe();
    }
    if (this.scrollSpeed !== 100 && type !== 'pause') {
      this.intervalScroll = interval((this.scrollSpeed * 60) / 60).subscribe(
        () => {
          this.textarea.nativeElement.scrollTop += 1;
        }
      );
    }
  }

  public countdownTimer(event): void {
    if (event.action === 'restart') {
      this.isTenSecondsLeft = false;
    }
    if (event.action === 'notify') {
      this.isTenSecondsLeft = true;
    }
    if (event.action === 'done') {
      this.stopRecording();
      this.isTenSecondsLeft = false;
    }
  }

  public stopRecording(): void {
    this.isRecorded = true;
    this.isRecording = false;
    this.mediaRecorder.stop();
    this.onDataAvailableEvent();
    this.countdownTop.restart();
    this.isTeleprompter = false;
    if (this.intervalScroll) {
      this.intervalScroll.unsubscribe();
    }
  }

  public closeRecorder() {
    this.isModalError = false;
    if (this.stm) {
      this.stm.getTracks().forEach((t) => t.stop());
    }
    if (this.intervalScroll) {
      this.intervalScroll.unsubscribe();
    }
    this.videoElemet.srcObject = null;
    this.closeVideo.emit(true);
  }

  public saveVideoToServer() {
    const file = new File([this.blob], 'record.mp4');
    this.saveVideo.emit(file);
    this.closeRecorder();
  }

  startDrag(event: MouseEvent | TouchEvent) {
    event.preventDefault();
    this.isDragging = true;
    this.isResizing = false;

    const clientX =
      (event as MouseEvent).clientX ?? (event as TouchEvent).touches[0].clientX;
    const clientY =
      (event as MouseEvent).clientY ?? (event as TouchEvent).touches[0].clientY;

    this.startLeft = this.lastLeft;
    this.startTop = this.lastTop;
    this.initialX = clientX;
    this.initialY = clientY;
  }

  startResize(event: MouseEvent | TouchEvent, direction: string) {
    event.preventDefault();
    this.isResizing = true;
    this.isDragging = false;
    this.resizeDirection = direction;

    const clientX =
      (event as MouseEvent).clientX ?? (event as TouchEvent).touches[0].clientX;
    const clientY =
      (event as MouseEvent).clientY ?? (event as TouchEvent).touches[0].clientY;

    this.initialX = clientX;
    this.initialY = clientY;

    const rect = this.textarea.nativeElement.getBoundingClientRect();
    this.initialWidth = rect.width;
    this.initialHeight = rect.height;
  }

  @HostListener('document:mousemove', ['$event'])
  @HostListener('document:touchmove', ['$event'])
  onMove(event: MouseEvent | TouchEvent) {
    if (!this.isDragging && !this.isResizing) {
      return;
    }

    event?.preventDefault();

    const clientX =
      (event as MouseEvent).clientX ?? (event as TouchEvent).touches[0].clientX;
    const clientY =
      (event as MouseEvent).clientY ?? (event as TouchEvent).touches[0].clientY;

    const deltaX = clientX - this.initialX;
    const deltaY = clientY - this.initialY;
    const containerRect = this.recorderWrapperEl.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;
    const textareaRect = this.textarea.nativeElement.getBoundingClientRect();
    const textareaWidth = textareaRect.width;
    const textareaHeight = textareaRect.height;
    const lastRight =
      containerWidth - (this.lastLeft + textareaWidth) > 9
        ? containerWidth - (this.lastLeft + textareaWidth)
        : 10;
    let lastBottom =
      containerHeight - (this.lastTop + textareaHeight) > 9
        ? containerHeight - (this.lastTop + textareaHeight)
        : 10;

    if (this.isDragging) {
      this.lastLeft = this.startLeft + deltaX;
      this.lastLeft = Math.max(
        0,
        Math.min(this.lastLeft, containerWidth - textareaWidth)
      );
      this.lastTop = this.startTop + deltaY;
      lastBottom = containerHeight - (this.lastTop + textareaHeight);
      if (this.lastLeft <= 10) {
        this.textareaContainer.nativeElement.style.left = '10px';
        this.lastLeft = 10;
      } else if (lastRight > 9) {
        this.textareaContainer.nativeElement.style.left = `${this.lastLeft}px`;
      }

      if (this.lastTop <= 0) {
        this.textareaContainer.nativeElement.style.top = '0px';
        this.lastTop = 0;
      } else if (lastBottom > 9) {
        this.textareaContainer.nativeElement.style.top = `${this.lastTop}px`;
      }
    }

    if (this.isResizing) {
      let newWidth = this.initialWidth;
      let newHeight = this.initialHeight;

      if (this.resizeDirection === 'right') {
        newWidth =
          Math.min(this.initialWidth + deltaX, containerWidth - this.lastLeft) -
          10;
      } else if (this.resizeDirection === 'bottom') {
        newHeight =
          Math.min(
            this.initialHeight + deltaY,
            containerHeight - this.lastTop
          ) <= 400
            ? Math.min(
                this.initialHeight + deltaY,
                containerHeight - this.lastTop
              )
            : 400;
      }

      this.textarea.nativeElement.style.width = `${newWidth}px`;
      this.textarea.nativeElement.style.height = `${newHeight}px`;
      this.isTeleprompterHasScroll =
        this.textarea.nativeElement.scrollHeight >
        this.textarea.nativeElement.clientHeight;
    }
    if (lastRight < 9) {
      this.lastLeft = containerWidth - textareaWidth + 10;
      this.textareaContainer.nativeElement.style.left = `${this.lastLeft}px`;
    }
    if (lastBottom < 60 && lastBottom >= 10) {
      this.textareaContainer.nativeElement.style.top = `${this.lastTop}px`;
    }
  }

  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  onMouseUp(): void {
    this.isDragging = false;
    this.isResizing = false;
  }

  toggleTeleprompterEdit() {
    this.isTeleprompterEditing = !this.isTeleprompterEditing;
  }

  changeTeleprompterFontSize(type: string) {
    switch (type) {
      case 'increase':
        this.fontSize += 1;
        break;
      case 'decrease':
        this.fontSize -= 1;
    }
  }

  changeTeleprompterSpeed(type: string) {
    switch (type) {
      case 'increase':
        if (this.scrollSpeed > 0) {
          this.scrollSpeed -= 5;
          this.scrollTeleprompter();
        }
        break;
      case 'decrease':
        if (this.scrollSpeed < 100) {
          this.scrollSpeed += 5;
          this.scrollTeleprompter();
        }
        break;
      case 'play':
        if (this.scrollSpeed === 100) {
          this.scrollSpeed = 95;
        }
        this.scrollTeleprompter();
        break;
      case 'pause':
        this.scrollTeleprompter('pause');
        break;
    }
  }

  @HostListener('document:fullscreenchange')
  @HostListener('document:webkitfullscreenchange')
  @HostListener('document:mozfullscreenchange')
  @HostListener('document:MSFullscreenChange')
  onFullscreenChange() {
    if (isPlatformBrowser(this.platformId) && !document.fullscreenElement) {
      this.closeRecorder();
    }
  }

  async enterFullscreen() {
    const element = this.recorderWrapper?.nativeElement;
    if (this.isMobile) {
      try {
        await this.recorderWrapper.nativeElement.requestFullscreen();
        if (element.requestFullscreen) {
          element.requestFullscreen();
        } else if ((element as any).webkitRequestFullscreen) {
          (element as any).webkitRequestFullscreen();
        } else if ((element as any).mozRequestFullScreen) {
          (element as any).mozRequestFullScreen();
        }
      } catch (err) {
        throwError(err);
      }
    }
  }

  private showModalError(error: string) {
    if (
      error.includes('denied permission') ||
      error.includes('Permission denied') ||
      error.includes('NotAllowedError') ||
      error.includes('NotFoundError')
    ) {
      this.modalErrorText = RecorderErrors.PERMISSION_DENIED;
      this.isPermissionDenied = true;
    } else if (error === 'noStream') {
      this.modalErrorText = RecorderErrors.NO_STREAM;
    } else if (error.includes('NotReadableError')) {
      this.modalErrorText = RecorderErrors.NOT_READABLE;
    } else {
      this.modalErrorText = error;
      this.isPermissionDenied = true;
    }
    this.isModalError = true;
  }

  private async startRecord(): Promise<void> {
    // Only attempt recording in browser environment
    if (!this.isBrowser) {
      this.showModalError(
        'Video recording is only available in browser environment'
      );
      return;
    }

    try {
      // Dynamic import to avoid SSR issues
      let MediaRecorder;
      if (this.isBrowser) {
        const module = await import('extendable-media-recorder');
        MediaRecorder = module.MediaRecorder;
      } else {
      }

      let options;
      if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) {
        options = { mimeType: 'video/webm;codecs=vp9' };
        this.mediaRecorder = new MediaRecorder(this.stm, options);
        this.mediaRecorder.start();
      } else if (MediaRecorder.isTypeSupported('video/webm;codecs=h264')) {
        options = { mimeType: 'video/webm;codecs=h264' };
        this.mediaRecorder = new MediaRecorder(this.stm, options);
        this.mediaRecorder.start();
      } else if (MediaRecorder.isTypeSupported('video/webm')) {
        options = { mimeType: 'video/webm' };
        this.mediaRecorder = new MediaRecorder(this.stm, options);
        this.mediaRecorder.start();
      } else if (MediaRecorder.isTypeSupported('video/mp4')) {
        options = { mimeType: 'video/mp4' };
        this.mediaRecorder = new MediaRecorder(this.stm, options);
        this.mediaRecorder.start();
      } else {
        this.mediaRecorder = new MediaRecorder(this.stm);
        this.mediaRecorder.start();
      }
      this.isRecorded = false;
      this.isRecording = true;
      this.countdownTop.begin();
      setTimeout(() => {
        this.changeTeleprompterSpeed('play');
      }, 5000);
    } catch (error) {
      console.error('Error starting video recording:', error);
      this.showModalError('Failed to initialize video recording');
    }
  }

  private resetTeleprompterScroll(): void {
    if (this.intervalScroll) {
      this.intervalScroll.unsubscribe();
    }
    this.textarea.nativeElement.scrollTop = 0;
  }

  private setPreviewVideo(): void {
    if (isPlatformBrowser(this.platformId)) {
      const isInLandscape = window.matchMedia(
        '(orientation: landscape)'
      ).matches;
      this.videoRecordedEl = this.videoRecorded.nativeElement;
      this.blob = new Blob(this.chunks, {
        type: this.mediaRecorder.mimeType
      });
      const url = URL.createObjectURL(this.blob);
      this.videoRecordedEl.src = url;
      this.videoRecordedEl.onplaying = () => {
        this.isPlaying = true;
      };
      this.videoRecordedEl.onpause = () => {
        this.isPlaying = false;
      };
      if (window.screen.width < 900 && isInLandscape) {
        this.videoRecordedEl.play();
      }
    }
  }

  private onDataAvailableEvent() {
    try {
      this.chunks = [];
      this.mediaRecorder.ondataavailable = (e: { data: any }) => {
        if (e.data.size > 0) {
          this.chunks.push(e.data);
        }
        this.setPreviewVideo();
      };
    } catch (error) {
      console.log(error);
    }
  }
}
